import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, RefreshCw, UserPlus, Shield, Users as UsersIcon, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import pb from '../lib/pocketbaseClient';

// Allowed team roles
const ALLOWED_ROLES = ['admin', 'super_admin', 'manager', 'sales', 'designer', 'hr', 'creator'];

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  super_admin: 'bg-purple-100 text-purple-700 border-purple-200',
  manager: 'bg-blue-100 text-blue-700 border-blue-200',
  sales: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  designer: 'bg-amber-100 text-amber-700 border-amber-200',
  hr: 'bg-pink-100 text-pink-700 border-pink-200',
  creator: 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
    role: 'sales',
  });
  const [submitting, setSubmitting] = useState(false);

  // ─── Fetch team members ──────────────────────────────────
  const fetchUsers = async () => {
    try {
      const records = await pb.collection('users').getFullList({
        sort: '-created',
      });
      const team = records.filter((user) => ALLOWED_ROLES.includes(user.role));
      setUsers(team);
    } catch (err) {
      console.error('Failed to fetch users', err);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ─── Add new user ────────────────────────────────────────
  const handleAddUser = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.passwordConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        passwordConfirm: formData.passwordConfirm,
        role: formData.role,
      };
      await pb.collection('users').create(data);
      toast.success('Team member added successfully');
      resetAddForm();
      fetchUsers();
    } catch (err) {
      console.error('Add user error:', err);
      toast.error(err.message || 'Failed to add user');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Edit user ────────────────────────────────────────────
  const handleEditUser = async (e) => {
    e.preventDefault();
    // If password fields are filled, validate; otherwise ignore password updates
    const updateData = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
    };
    if (formData.password) {
      if (formData.password !== formData.passwordConfirm) {
        toast.error('Passwords do not match');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      updateData.password = formData.password;
      updateData.passwordConfirm = formData.passwordConfirm;
    }

    setSubmitting(true);
    try {
      await pb.collection('users').update(editingUser.id, updateData);
      toast.success('Team member updated');
      resetEditForm();
      fetchUsers();
    } catch (err) {
      console.error('Update error:', err);
      toast.error(err.message || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Delete user ──────────────────────────────────────────
  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await pb.collection('users').delete(id);
      setUsers(users.filter((u) => u.id !== id));
      toast.success('User deleted');
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  // ─── Form helpers ─────────────────────────────────────────
  const resetAddForm = () => {
    setIsAddDialogOpen(false);
    setFormData({ name: '', email: '', password: '', passwordConfirm: '', role: 'sales' });
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      passwordConfirm: '',
      role: user.role || 'sales',
    });
    setIsEditDialogOpen(true);
  };

  const resetEditForm = () => {
    setIsEditDialogOpen(false);
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', passwordConfirm: '', role: 'sales' });
  };

  // ─── Role badge component ──────────────────────────────
  const RoleBadge = ({ role }) => {
    const color = ROLE_COLORS[role] || 'bg-gray-100 text-gray-700';
    return <Badge className={`${color} border`}>{role}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <UsersIcon className="w-8 h-8" /> Team Management
            </h1>
            <p className="text-white/80">Manage admin, sales, designers, and other team members</p>
          </div>
          <Button
            onClick={() => setIsAddDialogOpen(true)}
            className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white shadow-lg gap-2"
          >
            <UserPlus className="w-4 h-4" /> Add Team Member
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="text-slate-600 font-semibold">Name</TableHead>
                <TableHead className="text-slate-600 font-semibold">Email</TableHead>
                <TableHead className="text-slate-600 font-semibold">Role</TableHead>
                <TableHead className="text-slate-600 font-semibold">Joined</TableHead>
                <TableHead className="text-slate-600 font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-slate-400">
                    No team members found. Add your first team member.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} className="hover:bg-indigo-50/50 transition">
                    <TableCell className="font-medium">{user.name || '—'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <RoleBadge role={user.role} />
                    </TableCell>
                    <TableCell>
                      {new Date(user.created).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                          className="text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteUser(user.id, user.name)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ─── Add User Dialog ─────────────────────────────── */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-indigo-600 flex items-center gap-2">
              <UserPlus className="w-6 h-6" /> Add Team Member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser}>
            <div className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  placeholder="e.g., John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  placeholder="Min 6 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Confirm Password *</Label>
                <Input
                  type="password"
                  placeholder="Re-enter password"
                  value={formData.passwordConfirm}
                  onChange={(e) => setFormData({ ...formData, passwordConfirm: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(val) => setFormData({ ...formData, role: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ALLOWED_ROLES.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">
                  Assign a role to control access permissions.
                </p>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={resetAddForm}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                disabled={submitting}
              >
                {submitting ? 'Adding...' : 'Add Member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── Edit User Dialog ────────────────────────────── */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-indigo-600 flex items-center gap-2">
              <Edit className="w-6 h-6" /> Edit Team Member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditUser}>
            <div className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  placeholder="e.g., John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>New Password (leave blank to keep current)</Label>
                <Input
                  type="password"
                  placeholder="Min 6 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  placeholder="Re-enter new password"
                  value={formData.passwordConfirm}
                  onChange={(e) => setFormData({ ...formData, passwordConfirm: e.target.value })}
                />
              </div>
              <div>
                <Label>Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(val) => setFormData({ ...formData, role: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ALLOWED_ROLES.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">
                  Assign a role to control access permissions.
                </p>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={resetEditForm}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;